import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';

export type CelebrationTheme =
  | 'fireworks'
  | 'confetti'
  | 'snowflakes'
  | 'hearts'
  | 'stars'
  | 'waves';

export interface SpecialDay {
  id: string;
  name: string;           // e.g. "New Year 2026"
  message: string;        // e.g. "Happy New Year! 🎉 Enjoy 20% off all batteries"
  subMessage: string;     // smaller subtitle
  theme: CelebrationTheme;
  themeColor: string;     // hex color for accent
  emoji: string;          // e.g. "🎉"
  active: boolean;
  startDate: string;      // ISO date
  endDate: string;        // ISO date
  showBanner: boolean;
  dismissible: boolean;
}

interface CelebrationContextValue {
  specialDays: SpecialDay[];
  addSpecialDay: (d: Omit<SpecialDay, 'id'>) => void;
  updateSpecialDay: (d: SpecialDay) => void;
  deleteSpecialDay: (id: string) => void;
  toggleSpecialDay: (id: string) => void;
  activeDay: SpecialDay | null;
}

const STORAGE_KEY = 'stingray_special_days';
const CelebrationContext = createContext<CelebrationContextValue | null>(null);

function load(): SpecialDay[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw) as SpecialDay[];
  } catch { /* ignore */ }
  return [];
}

export function CelebrationProvider({ children }: { children: ReactNode }) {
  const [specialDays, setSpecialDays] = useState<SpecialDay[]>(load);

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(specialDays)); } catch { /* ignore */ }
  }, [specialDays]);

  const addSpecialDay = useCallback((d: Omit<SpecialDay, 'id'>) => {
    setSpecialDays((prev) => [...prev, { ...d, id: 'day-' + Date.now().toString(36) }]);
  }, []);

  const updateSpecialDay = useCallback((d: SpecialDay) => {
    setSpecialDays((prev) => prev.map((x) => (x.id === d.id ? d : x)));
  }, []);

  const deleteSpecialDay = useCallback((id: string) => {
    setSpecialDays((prev) => prev.filter((x) => x.id !== id));
  }, []);

  const toggleSpecialDay = useCallback((id: string) => {
    setSpecialDays((prev) => prev.map((x) => (x.id === id ? { ...x, active: !x.active } : x)));
  }, []);

  const activeDay: SpecialDay | null = (() => {
    const now = new Date();
    return (
      specialDays.find((d) => {
        if (!d.active || !d.showBanner) return false;
        const start = new Date(d.startDate);
        const end = new Date(d.endDate);
        return now >= start && now <= end;
      }) || null
    );
  })();

  return (
    <CelebrationContext.Provider value={{ specialDays, addSpecialDay, updateSpecialDay, deleteSpecialDay, toggleSpecialDay, activeDay }}>
      {children}
    </CelebrationContext.Provider>
  );
}

export function useCelebration() {
  const ctx = useContext(CelebrationContext);
  if (!ctx) throw new Error('useCelebration must be used within CelebrationProvider');
  return ctx;
}
